import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { SensorPropertiesService } from '@services/sensor-properties.service';
import { WaterRegisterFormatHelper } from '@helpers/water-register-format.helper';

export interface PulseAdvancedChangeEvent {
  deviceSensorId: string;
  isDirty: boolean;
  valuesToUpsert: Record<string, any> | null;
}

@Component({
  selector: 'app-pulse-water-register',
  templateUrl: './pulse-water-register.component.html',
  styleUrls: ['./pulse-water-register.component.scss'],
  standalone: false,
})
export class PulseWaterRegisterComponent implements OnChanges {

  @Input() deviceSensorId!: string;
  @Input() commitToken: number = 0;

  @Output() changeState = new EventEmitter<PulseAdvancedChangeEvent>();

  form: UntypedFormGroup;

  loaded = false;
  loading = false;

  private initialSnapshotJson: string = '';

  currentDigits = WaterRegisterFormatHelper.DEFAULT_DIGITS;
  currentDecimals = WaterRegisterFormatHelper.DEFAULT_DECIMALS;
  currentWaterRegisterInt = 0;

  currentPreview = '';
  newPreview = '';
  syncPreview = '';

  constructor(
    private fb: UntypedFormBuilder,
    private sensorPropsService: SensorPropertiesService,
  ) {
    this.form = this.fb.group({
      digits: [
        WaterRegisterFormatHelper.DEFAULT_DIGITS,
        [Validators.required, Validators.min(1), Validators.max(8)],
      ],
      decimals: [
        WaterRegisterFormatHelper.DEFAULT_DECIMALS,
        [Validators.required, Validators.min(0), Validators.max(3)],
      ],
      syncValueHuman: [''],
    });

    this.form.valueChanges.subscribe(() => {
      this.refreshPreviews();
      this.emitState();
    });

    // Cuando cambian los decimales
    this.form.get('decimals')?.valueChanges.subscribe(() => {
      this.trimSyncDecimals();
      this.form.get('syncValueHuman')?.updateValueAndValidity({ emitEvent: false });
    });

    // ✅ Cuando cambian los dígitos (enteros)
    this.form.get('digits')?.valueChanges.subscribe(() => {
      this.trimSyncDecimals();
      this.form.get('syncValueHuman')?.updateValueAndValidity({ emitEvent: false });
    });
  }

  // -----------------------------
  // 🔹 LIMITADOR DE DECIMALES
  // -----------------------------
  onSyncInput(ev: Event): void {
    const input = ev.target as HTMLInputElement;

    const digits = WaterRegisterFormatHelper.clampDigits(
      this.form.get('digits')?.value
    );
    const decimals = WaterRegisterFormatHelper.clampDecimals(
      this.form.get('decimals')?.value
    );

    let v = input.value ?? '';

    // Normaliza coma a punto
    v = v.replace(',', '.');

    // Solo números y un punto
    v = v.replace(/[^\d.]/g, '');

    // Deja solo el primer punto
    const firstDot = v.indexOf('.');
    if (firstDot !== -1) {
      v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, '');
    }

    // Separa partes
    let intPart = '';
    let decPart = '';

    if (v.includes('.')) {
      const parts = v.split('.');
      intPart = parts[0] ?? '';
      decPart = parts[1] ?? '';
    } else {
      intPart = v;
    }

    // ✅ Limita ENTEROS (izquierda) a "digits"
    if (digits > 0) {
      intPart = intPart.slice(0, digits);
    }

    // ✅ Limita DECIMALES (derecha) a "decimals"
    if (decimals === 0) {
      decPart = '';
    } else {
      decPart = decPart.slice(0, decimals);
    }

    // Reconstruye
    v = decimals > 0 && (v.includes('.') || decPart.length > 0)
      ? `${intPart}.${decPart}`
      : intPart;

    // Refleja en input + form
    if (input.value !== v) input.value = v;
    this.form.get('syncValueHuman')?.setValue(v, { emitEvent: true });
  }
  // Si el usuario cambia decimals después de haber escrito valor
  private trimSyncDecimals(): void {
    const control = this.form.get('syncValueHuman');
    if (!control) return;

    const digits = WaterRegisterFormatHelper.clampDigits(
      this.form.get('digits')?.value
    );
    const decimals = WaterRegisterFormatHelper.clampDecimals(
      this.form.get('decimals')?.value
    );

    let v = String(control.value ?? '').replace(',', '.');
    v = v.replace(/[^\d.]/g, '');

    // solo primer punto
    const firstDot = v.indexOf('.');
    if (firstDot !== -1) {
      v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, '');
    }

    let intPart = '';
    let decPart = '';

    if (v.includes('.')) {
      const parts = v.split('.');
      intPart = parts[0] ?? '';
      decPart = parts[1] ?? '';
    } else {
      intPart = v;
    }

    // ✅ recorta enteros
    if (digits > 0) intPart = intPart.slice(0, digits);

    // ✅ recorta decimales
    if (decimals === 0) decPart = '';
    else decPart = decPart.slice(0, decimals);

    v = decimals > 0 && (v.includes('.') || decPart.length > 0)
      ? `${intPart}.${decPart}`
      : intPart;

    control.setValue(v, { emitEvent: false });
  }

  // -----------------------------

  onOpened(): void {
    if (this.loaded || this.loading) return;
    if (!this.deviceSensorId) return;

    this.loading = true;

    this.sensorPropsService
      .getProperties(this.deviceSensorId)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res) => {
          const values = res?.values ?? {};

          const digits = WaterRegisterFormatHelper.clampDigits(values['digits']);
          const decimals = WaterRegisterFormatHelper.clampDecimals(values['decimals']);
          const waterRegisterInt = Number(values['waterRegister']);

          this.currentDigits = digits;
          this.currentDecimals = decimals;
          this.currentWaterRegisterInt = Number.isFinite(waterRegisterInt)
            ? waterRegisterInt
            : 0;

          this.form.patchValue(
            {
              digits,
              decimals,
              syncValueHuman: '',
            },
            { emitEvent: false },
          );

          this.loaded = true;

          this.refreshPreviews();
          this.captureSnapshotAsInitial();
          this.emitState();
        },
        error: () => {
          this.loaded = true;
          this.refreshPreviews();
          this.captureSnapshotAsInitial();
          this.emitState();
        },
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['commitToken'] && !changes['commitToken'].firstChange) {
      this.form.patchValue({ syncValueHuman: '' }, { emitEvent: false });
      this.captureSnapshotAsInitial();
      this.refreshPreviews();
      this.emitState();
    }
  }

  private refreshPreviews(): void {
    const digits = WaterRegisterFormatHelper.clampDigits(this.form.get('digits')?.value);
    const decimals = WaterRegisterFormatHelper.clampDecimals(this.form.get('decimals')?.value);

    this.currentPreview = WaterRegisterFormatHelper.formatScaledInt(
      this.currentWaterRegisterInt,
      { digits: this.currentDigits, decimals: this.currentDecimals },
    );

    this.newPreview = WaterRegisterFormatHelper.formatScaledInt(
      this.currentWaterRegisterInt,
      { digits, decimals },
    );

    const syncHuman = this.form.get('syncValueHuman')?.value;
    const scaled = WaterRegisterFormatHelper.parseHumanToScaledInt(syncHuman, decimals);

    this.syncPreview =
      scaled === null
        ? ''
        : WaterRegisterFormatHelper.formatScaledInt(scaled, { digits, decimals });
  }

  private captureSnapshotAsInitial(): void {
    const snapshot = this.form.getRawValue();
    this.initialSnapshotJson = JSON.stringify(snapshot);
    this.form.markAsPristine();
  }

  private emitState(): void {
    if (!this.deviceSensorId) return;

    const isDirty = this.isReallyDirty();
    const valuesToUpsert = isDirty ? this.buildValuesToUpsert() : null;

    this.changeState.emit({
      deviceSensorId: this.deviceSensorId,
      isDirty,
      valuesToUpsert,
    });
  }

  private isReallyDirty(): boolean {
    const currentJson = JSON.stringify(this.form.getRawValue());
    return currentJson !== this.initialSnapshotJson;
  }

  private buildValuesToUpsert(): Record<string, any> {
    const digits = WaterRegisterFormatHelper.clampDigits(this.form.get('digits')?.value);
    const decimals = WaterRegisterFormatHelper.clampDecimals(this.form.get('decimals')?.value);

    const values: Record<string, any> = {
      digits,
      decimals,
    };

    const syncHuman = this.form.get('syncValueHuman')?.value;
    const scaled = WaterRegisterFormatHelper.parseHumanToScaledInt(syncHuman, decimals);

    if (scaled !== null) {
      values['waterRegister'] = String(scaled);
    }

    return values;
  }

  get currentFormatLabel(): string {
    return WaterRegisterFormatHelper.formatLabel({
      digits: this.currentDigits,
      decimals: this.currentDecimals,
    });
  }

  get newFormatLabel(): string {
    const digits = WaterRegisterFormatHelper.clampDigits(this.form.get('digits')?.value);
    const decimals = WaterRegisterFormatHelper.clampDecimals(this.form.get('decimals')?.value);
    return WaterRegisterFormatHelper.formatLabel({ digits, decimals });
  }

  get currentIntegerDigits(): string[] {
    return (this.currentPreview || '0').split('.')[0].split('');
  }

  get currentDecimalDigits(): string[] {
    return (this.currentPreview || '').split('.')[1]?.split('') ?? [];
  }
}
